import { ApiRequestOptions } from '@api/core/ApiRequestOptions';
import { getRequestOptions } from '@api/core/utils';

import type { DeparturePointViewModel } from '../models/DeparturePointViewModel';
import type { RecommendedToursViewModel } from '../models/RecommendedToursViewModel';
import type { TourDetailViewModel } from '../models/TourDetailViewModel';
import type { TourExtraServiceSearchViewModel } from '../models/TourExtraServiceSearchViewModel';
import type { TourListViewModel } from '../models/TourListViewModel';
import type { TourPassportsViewModel } from '../models/TourPassportsViewModel';
import type { TourPreCalculateRequestViewModel } from '../models/TourPreCalculateRequestViewModel';
import type { TourPreCalculateViewModel } from '../models/TourPreCalculateViewModel';
import type { TourRecommendationsRequestViewModel } from '../models/TourRecommendationsRequestViewModel';
import type { CancelablePromise } from '../core/CancelablePromise';
import { request as __request } from '../core/request';

export class ToursService {
  /**
   * List
   * Gets all tours by slug
   * @returns TourListViewModel Success
   * @throws ApiError
   */
  public static async listTours(
    {
      keywords,
      slug,
      sort,
      region,
      theme,
      feature,
      date,
      segment,
      status,
      priceMin,
      priceMax,
      durationMin,
      durationMax,
      page,
      size,
    }: {
      keywords?: string;
      slug?: string;
      /**
       * Sort: popularity, priceasc or pricedesc, Default: popularity
       */
      sort?: any;
      region?: Array<number>;
      theme?: Array<number>;
      feature?: Array<number>;
      date?: Array<number>;
      segment?: Array<number>;
      status?: boolean;
      priceMin?: number;
      priceMax?: number;
      durationMin?: number;
      durationMax?: number;
      page?: number;
      size?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourListViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TourListViewModel>({
      method: 'GET',
      url: `/v2/tours`,
      query: {
        ...(keywords ? { keywords: keywords } : {}),
        ...(slug ? { slug: slug } : {}),
        ...(sort ? { sort: sort } : {}),
        ...(region ? { region: region } : {}),
        ...(theme ? { theme: theme } : {}),
        ...(feature ? { feature: feature } : {}),
        ...(date ? { date: date } : {}),
        ...(segment ? { segment: segment } : {}),
        ...(status ? { status: status } : {}),
        ...(priceMin ? { priceMin: priceMin } : {}),
        ...(priceMax ? { priceMax: priceMax } : {}),
        ...(durationMin ? { durationMin: durationMin } : {}),
        ...(durationMax ? { durationMax: durationMax } : {}),
        ...(page ? { page: page } : {}),
        ...(size ? { size: size } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Detail
   * Gets a tour details by slug
   * @returns TourDetailViewModel Success
   * @throws ApiError
   */
  public static async getTour(
    {
      slug,
      date,
      basketId,
    }: {
      /**
       * Tour Slug
       */
      slug: string;
      /**
       * Tour Dates
       */
      date?: Array<number>;
      /**
       * Basket Id
       */
      basketId?: string;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourDetailViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TourDetailViewModel>({
      method: 'GET',
      url: `/v2/tours/${slug}`,
      path: {
        ...(slug ? { slug: slug } : {}),
      },
      headers: {
        ...(basketId ? { basketId: basketId } : {}),
        ...getRequestOptions(computedHeaders, 'headers'),
      },
      query: {
        ...(date ? { date: date } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Precalculate
   * Precalculate  tour package price
   * @returns TourPreCalculateViewModel Success
   * @throws ApiError
   */
  public static async tourPrecalculate(
    {
      body,
    }: {
      /**
       * Tour precalculate request model
       */
      body?: TourPreCalculateRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourPreCalculateViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TourPreCalculateViewModel>({
      method: 'POST',
      url: `/v2/tours/precalculate`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Departure Points
   * Gets departure points by tour id and detail id
   * @returns DeparturePointViewModel Success
   * @throws ApiError
   */
  public static async departurePoints(
    {
      tourId,
      detailId,
    }: {
      tourId: number;
      detailId: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<Array<DeparturePointViewModel>> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<Array<DeparturePointViewModel>>({
      method: 'GET',
      url: `/v2/tours/departures/${tourId}/${detailId}`,
      path: {
        ...(tourId ? { tourId: tourId } : {}),
        ...(detailId ? { detailId: detailId } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Passports
   * Gets passport types
   * @returns TourPassportsViewModel Success
   * @throws ApiError
   */
  public static async passports(
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourPassportsViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TourPassportsViewModel>({
      method: 'GET',
      url: `/v2/tours/passports`,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Extra Services (Search)
   * Gets extra services for tour
   * @returns TourExtraServiceSearchViewModel Success
   * @throws ApiError
   */
  public static async extraServiceSearch(
    {
      saleDate,
      startDate,
      endDate,
      tourId,
      numberofPeople,
    }: {
      saleDate?: string;
      startDate?: string;
      endDate?: string;
      tourId?: number;
      numberofPeople?: number;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<TourExtraServiceSearchViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<TourExtraServiceSearchViewModel>({
      method: 'GET',
      url: `/v2/tours/search/extraservice`,
      query: {
        ...(saleDate ? { saleDate: saleDate } : {}),
        ...(startDate ? { startDate: startDate } : {}),
        ...(endDate ? { endDate: endDate } : {}),
        ...(tourId ? { tourId: tourId } : {}),
        ...(numberofPeople ? { numberofPeople: numberofPeople } : {}),
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }

  /**
   * Recommendations (Tour)
   * Gets recommendations for tours.
   * @returns RecommendedToursViewModel Success
   * @throws ApiError
   */
  public static async tourRecommendations(
    {
      body,
    }: {
      body?: TourRecommendationsRequestViewModel;
    },
    options?: Partial<ApiRequestOptions>
  ): CancelablePromise<RecommendedToursViewModel> {
    const { cookies = {}, headers = {} } = options ?? {};
    const auth = headers.authorization || cookies.token;
    const computedHeaders = { ...headers, authorization: auth };
    delete cookies.token;
    const result = await __request<RecommendedToursViewModel>({
      method: 'POST',
      url: `/v2/tours/recommendations`,
      body: body,
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `Forbidden`,
        404: `Not Found`,
        500: `Server Error`,
      },
      headers: getRequestOptions(computedHeaders, 'headers'),
      cookies: { ...getRequestOptions(cookies, 'cookies') },
    });
    return result;
  }
}

declare module '@setur/api/v2' {
  interface Services {
    ToursService: typeof ToursService;
  }
}
